import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TrendingUp,
  DollarSign,
  PieChart,
  Building2,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Target,
  Calendar,
  Mail,
  MapPin,
} from 'lucide-react';
import {
  fetchLeadershipReport,
  parseLeadershipReportResponse,
} from '@/services/leadershipReportAPI';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function formatCurrency(n) {
  if (n == null || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return String(n);
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function countBy(items, keyFn) {
  const m = new Map();
  for (const item of items) {
    const k = keyFn(item);
    if (!k || k === '—') continue;
    m.set(k, (m.get(k) || 0) + 1);
  }
  return Array.from(m.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
}

const LeadershipDealsSection = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchLeadershipReport({ pageNumber, pageSize });
      const parsed = parseLeadershipReportResponse(json);
      setRows(parsed.rows);
      setTotal(parsed.total);
    } catch (e) {
      setError(e?.message || 'Could not load leadership report.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const purposeOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.loanPurpose).filter((p) => p && p !== '—'));
    return Array.from(set).sort();
  }, [rows]);

  const industryOptions = useMemo(() => {
    const set = new Set(
      rows.map((r) => r.businessIndustry).filter((p) => p && p !== '—')
    );
    return Array.from(set).sort();
  }, [rows]);

  const statusOptions = useMemo(() => {
    const set = new Set(
      rows.map((r) => r.statusLabel).filter((p) => p && p !== '—')
    );
    return Array.from(set).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (purposeFilter !== 'all' && r.loanPurpose !== purposeFilter) return false;
      if (industryFilter !== 'all' && r.businessIndustry !== industryFilter) return false;
      if (statusFilter !== 'all' && r.statusLabel !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        r.businessName,
        r.contactName,
        r.email,
        r.loanPurpose,
        r.businessType,
        r.businessIndustry,
        r.city,
        r.state,
        r.statusLabel,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, purposeFilter, industryFilter, statusFilter]);

  const metrics = useMemo(() => {
    const withAmount = filteredRows.filter((r) => r.loanAmountNum != null);
    const sum = withAmount.reduce((acc, r) => acc + (r.loanAmountNum || 0), 0);
    const avg = withAmount.length ? sum / withAmount.length : null;
    const purposes = countBy(filteredRows, (r) => r.loanPurpose);
    const industries = countBy(filteredRows, (r) => r.businessIndustry);
    return {
      pipelineCount: filteredRows.length,
      sumRequested: sum || null,
      avgDeal: avg,
      topPurposes: purposes,
      topIndustries: industries,
    };
  }, [filteredRows]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const statusBadgeClass = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('approv') || s.includes('complete') || s.includes('fund')) {
      return 'bg-emerald-600 text-white border-emerald-600';
    }
    if (s.includes('pend') || s.includes('review') || s.includes('queue')) {
      return 'bg-amber-500 text-white border-amber-500';
    }
    if (s.includes('declin') || s.includes('reject') || s.includes('cancel')) {
      return 'bg-red-600 text-white border-red-600';
    }
    return 'bg-slate-600 text-white border-slate-600';
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50/90 via-white to-teal-50/80 p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-emerald-800">
              <Sparkles className="h-5 w-5 shrink-0" />
              <span className="text-sm font-semibold uppercase tracking-wide">
                HalalCred leadership pipeline
              </span>
            </div>
            <h3 className="mt-1 text-lg font-semibold text-gray-900">
              Riba-free financing demand across your network
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-600">
              See how businesses are seeking Sharia-aligned capital: typical purposes, industries,
              and requested amounts—so partners can prioritize outreach and product fit.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
            onClick={() => load()}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4 text-sm text-red-800">{error}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-emerald-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pipeline (filtered)</p>
                <p className="mt-1 text-3xl font-bold text-gray-900">{metrics.pipelineCount}</p>
                <p className="mt-1 text-xs text-gray-500">Deals matching current filters</p>
              </div>
              <div className="rounded-lg bg-emerald-100 p-2">
                <Target className="h-6 w-6 text-emerald-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Requested capital (filtered)</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {formatCurrency(metrics.sumRequested)}
                </p>
                <p className="mt-1 text-xs text-gray-500">Sum of parsed amounts on this page</p>
              </div>
              <div className="rounded-lg bg-teal-100 p-2">
                <DollarSign className="h-6 w-6 text-teal-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. request (filtered)</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {formatCurrency(metrics.avgDeal)}
                </p>
                <p className="mt-1 text-xs text-gray-500">Among rows with a numeric amount</p>
              </div>
              <div className="rounded-lg bg-cyan-100 p-2">
                <TrendingUp className="h-6 w-6 text-cyan-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">API page</p>
                <p className="mt-1 text-3xl font-bold text-gray-900">
                  {pageNumber}
                  <span className="text-lg font-normal text-gray-400"> / {totalPages}</span>
                </p>
                <p className="mt-1 text-xs text-gray-500">{total} total records reported</p>
              </div>
              <div className="rounded-lg bg-slate-100 p-2">
                <PieChart className="h-6 w-6 text-slate-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <PieChart className="h-4 w-4 text-emerald-600" />
              Top financing purposes
            </CardTitle>
            <p className="text-xs text-gray-500">What applicants are trying to fund (filtered set)</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {metrics.topPurposes.length === 0 ? (
              <p className="text-sm text-gray-500">No purpose data for this filter.</p>
            ) : (
              metrics.topPurposes.map(([label, count]) => (
                <div key={label} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate text-gray-800" title={label}>
                    {label}
                  </span>
                  <Badge variant="secondary" className="shrink-0 font-mono">
                    {count}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-emerald-600" />
              Top industries
            </CardTitle>
            <p className="text-xs text-gray-500">Where halal financing demand clusters</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {metrics.topIndustries.length === 0 ? (
              <p className="text-sm text-gray-500">No industry data for this filter.</p>
            ) : (
              metrics.topIndustries.map(([label, count]) => (
                <div key={label} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate text-gray-800" title={label}>
                    {label}
                  </span>
                  <Badge variant="secondary" className="shrink-0 font-mono">
                    {count}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="border-b bg-gray-50/80 py-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base">Filters</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">Page size</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPageNumber(1);
                  }}
                >
                  <SelectTrigger className="h-9 w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search name, business, email, purpose…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={purposeFilter} onValueChange={setPurposeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Purpose" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All purposes</SelectItem>
                  {purposeOptions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={industryFilter} onValueChange={setIndustryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All industries</SelectItem>
                  {industryOptions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statusOptions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-emerald-600" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              <p className="font-medium text-gray-700">No deals match these filters</p>
              <p className="mt-1 text-sm">Try clearing search or widening purpose / industry.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Applicant</th>
                    <th className="px-4 py-3 font-medium">Business</th>
                    <th className="px-4 py-3 font-medium">Purpose</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRows.map((r) => (
                    <tr key={r.id} className="hover:bg-emerald-50/40">
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">{r.contactName}</div>
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                          <Mail className="h-3 w-3 shrink-0" />
                          <span className="truncate max-w-[200px]" title={r.email}>
                            {r.email}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">{r.businessName}</div>
                        <div className="text-xs text-gray-600">{r.businessType}</div>
                        <div className="text-xs text-gray-500">{r.businessIndustry}</div>
                        {(r.city || r.state) && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                            <MapPin className="h-3 w-3 shrink-0" />
                            {[r.city, r.state].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="max-w-[220px] px-4 py-3 align-top text-gray-800">
                        <span className="line-clamp-2" title={r.loanPurpose}>
                          {r.loanPurpose}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top font-medium text-gray-900">
                        {r.loanAmountNum != null ? formatCurrency(r.loanAmountNum) : r.loanAmountLabel}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Badge className={`${statusBadgeClass(r.statusLabel)} border font-medium`}>
                          {r.statusLabel}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-gray-400" />
                          {formatDate(r.submittedAt)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col items-center justify-between gap-3 border-t pt-4 sm:flex-row">
        <p className="text-xs text-gray-500">
          Showing {filteredRows.length} of {rows.length} loaded rows · Page {pageNumber} ({pageSize}{' '}
          per request)
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loading || pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loading || pageNumber >= totalPages}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LeadershipDealsSection;
